import { decode as cborDecode, encode as cborEncode } from 'cbor-x';
import { ICrypto, Signature } from './rpc.js';
import { crypto, CryptoKey, CryptoKeyPair } from 'ws-helper';
import { toBase64Url as b64encode, toBuffer as b64decode } from "b64u-lite";

export async function Ed25519PrivateToPublic(privateKey){
    const jwkPrivate = await crypto.subtle.exportKey("jwk", privateKey);    
    delete jwkPrivate.d;
    jwkPrivate.key_ops = ["verify"];
    return crypto.subtle.importKey("jwk", jwkPrivate, { name: 'Ed25519' }, true, ["verify"]);
}
export class DefaultSubtleCrypto implements ICrypto {
    nonexportableClientKey : CryptoKeyPair;
    clientKeyPublicKeyString : string;
    clientKeyPublicKeyRaw : Uint8Array;
    ecdhSessionKey : CryptoKeyPair;
    async init(clientKeyExportable: boolean, clientKey?: CryptoKeyPair) {
        if (clientKey) {
            this.nonexportableClientKey = clientKey;
        } else {
            let keyPair = await crypto.subtle.generateKey(
                { name: 'Ed25519' },
                clientKeyExportable,
                ["sign", "verify"],
            ) as CryptoKeyPair;
            this.nonexportableClientKey = keyPair;
            await this.storeClientKey(keyPair);
        }
        const pubKey = await crypto.subtle.exportKey("raw", this.nonexportableClientKey.publicKey);
        this.clientKeyPublicKeyRaw = new Uint8Array(pubKey);
        this.clientKeyPublicKeyString = b64encode(pubKey);
        console.log("Hello, my public key is ",this.clientKeyPublicKeyString);
        await this.initSessionKey();
    }
    async initSessionKey() {
        this.ecdhSessionKey = await crypto.subtle.generateKey({
            name: 'ECDH', namedCurve: 'P-256'
        }, false, ["deriveKey"]);
    }
    async storeClientKey(keyPair: CryptoKeyPair) {
        console.log("Generated new key pair...");
        console.log(b64encode(await crypto.subtle.exportKey('pkcs8', keyPair.privateKey)));
    }
    async verifyUnwrap(expectedDataType: string, signature: Signature): Promise<{ key: Uint8Array; data: any; }> {
        const [ publicKey, sig, wrappedData ] = signature;
        const [ dataType, data ] = cborDecode(wrappedData);
        if (dataType !== expectedDataType) throw "expected data type "+expectedDataType+", got "+dataType;
        const key = await crypto.subtle.importKey("raw", publicKey, { name: 'Ed25519' }, true, ['verify']);
        const okay = await crypto.subtle.verify({ name: 'Ed25519' }, key, sig, wrappedData);
        if (!okay) throw "signature verification failed";
        
        return { 'key': publicKey, 'data': data };
    }
    async keyExchangeRequest(data: any): Promise<[Uint8Array, any]> {
        const dhKey = await crypto.subtle.generateKey({
            name: 'ECDH', namedCurve: 'P-256'
        }, false, ["deriveKey"]);
        const pubKey = new Uint8Array(await crypto.subtle.exportKey('raw', dhKey.publicKey));
        return [pubKey, dhKey];
    }
    async keyExchange(peerKey: Uint8Array, dhKey: any): Promise<any> {
        const pubkey = await crypto.subtle.importKey('raw', peerKey, {name: 'ECDH', namedCurve: 'P-256'}, false, ['deriveKey']);
        return await crypto.subtle.deriveKey({name: 'ECDH', public: pubkey}, dhKey, {name: 'AES-GCM', length: 256}, false, ['encrypt', 'decrypt']);
    }
    wrapData(dataType: string, data: any): Uint8Array {
        return cborEncode([dataType, data]);
    }
    async signWithClientKey(dataType: string, data: any): Promise<Signature> {
        const wrappedData = this.wrapData(dataType, data);
        const sig = await this._signDataWithClientKey(wrappedData);
        return [
            this.clientKeyPublicKeyRaw,
            sig,
            wrappedData
        ];
    }
    async _signDataWithClientKey(wrappedData: Uint8Array): Promise<Uint8Array> {
        return new Uint8Array(await crypto.subtle.sign({ name: 'Ed25519' }, this.nonexportableClientKey.privateKey, wrappedData));
    }
    aencryptSession(sessionKey: any, data: Uint8Array): Promise<Uint8Array> {
        throw new Error('Method not implemented.');
    }
    adecryptSession(sessionKey: any, data: Uint8Array): Promise<Uint8Array> {
        throw new Error('Method not implemented.');
    }
}
export class NodeCrypto extends DefaultSubtleCrypto {
    async init() {
        let key = process.env.PRIVATE_KEY;
        if (key) {
            const privKey = await crypto.subtle.importKey("pkcs8", Buffer.from(key, 'base64url'), 
                    { name: 'Ed25519' },
                    true,
                    ["sign"])
            await super.init(true, { privateKey: privKey, publicKey: await Ed25519PrivateToPublic(privKey) });
        } else {
            await super.init(true);
        }
    }
    static async create() {
        const n = new NodeCrypto();
        await n.init();
        return n;
    }
}
